import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Cpu, Activity, SignalHigh } from 'lucide-react';
import { InterviewTerminal } from '@/components/interview/InterviewTerminal';
import { InterviewSetup } from '@/components/interview/InterviewSetup';

export function InterviewPage() {
 const { sessionId } = useParams<{ sessionId: string }>();
 const [searchParams] = useSearchParams();
 const navigate = useNavigate();
 const urlProfileId = searchParams.get('jobProfileId') || undefined;

 // Local state for the selected profile and started status
 const [selectedProfileId, setSelectedProfileId] = useState<string | undefined>(
  urlProfileId
 );
 const [isSessionActive, setIsSessionActive] = useState(false);
 const [interviewType, setInterviewType] = useState('technical_systems');

 // If no sessionId, generate one and redirect
 useEffect(() => {
  if (!sessionId) {
   const newSessionId = crypto.randomUUID();
   const params = urlProfileId ? `?jobProfileId=${urlProfileId}` : '';
   navigate(`/interview/${newSessionId}${params}`, { replace: true });
  }
 }, [sessionId, urlProfileId, navigate]);

 // Warn before unload if session is active
 useEffect(() => {
  const handleBeforeUnload = (e: BeforeUnloadEvent) => {
   if (isSessionActive) {
    e.preventDefault();
    e.returnValue = ''; // Trigger browser confirmation dialog
   }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);
  return () => window.removeEventListener('beforeunload', handleBeforeUnload);
 }, [isSessionActive]);

 // On mount, if we have a sessionId but the session isn't active (meaning it's a reload or fresh entry),
 // we want to generate a FRESH session ID to ensure a clean state, while keeping the profile.
 const hasRefreshedRef = useState(false); // Ref to track if we've done the initial refresh logic
 useEffect(() => {
  if (sessionId && !isSessionActive && !hasRefreshedRef[0]) {
   // It's a fresh load on an existing ID.
   // Generate new ID to "refresh" the session as requested by user.
   const newSessionId = crypto.randomUUID();
   const params = urlProfileId ? `?jobProfileId=${urlProfileId}` : '';
   // REPLACE the current URL so we don't go back to dashboard or keep stale ID
   navigate(`/interview/${newSessionId}${params}`, { replace: true });
   // Mark as refreshed so we don't loop endlessly
   // Note: navigate will re-mount the component or re-run effects depending on router config,
   // but since we replace, the new mount will have a new ID and hit the !sessionId check or just be valid.
  }
 }, []); // Run once on mount

 // Sync local profile state if URL param changes (e.g. navigation)
 useEffect(() => {
  if (urlProfileId) {
   setSelectedProfileId(urlProfileId);
  }
 }, [urlProfileId]);

 const handleStartSession = (profileId: string, type: string) => {
  setIsSessionActive(true);
  setSelectedProfileId(profileId);
  setInterviewType(type);
  // Persist the profile selection in URL so refresh keeps context
  const newParams = new URLSearchParams(searchParams);
  newParams.set('jobProfileId', profileId);
  navigate(`/interview/${sessionId}?${newParams.toString()}`, {
   replace: true,
  });
 };

 if (!sessionId) return null;

 return (
  <AppLayout>
   <div className='max-w-5xl mx-auto h-auto md:h-[70vh] min-h-[600px] flex flex-col'>
    {/* Header */}
    <div className='mb-6 border-b border-border pb-6 flex flex-col md:flex-row justify-between items-start md:items-end gap-4 md:gap-0'>
     <div>
      <div className='flex items-center gap-2 mb-2'>
       <div
        className={`w-2 h-2 rounded-full ${isSessionActive ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'}`}
       />
       <span className='font-mono text-xs text-muted-foreground uppercase'>
        {isSessionActive ? 'Live Simulation Node' : 'Session Configuration'}
       </span>
      </div>
      <h1 className='text-3xl font-bold tracking-tight mb-1'>INTERVIEW HUB</h1>
      <p className='text-muted-foreground font-mono text-sm'>
       Real-time interactive session with your AI copilot.
      </p>
     </div>
     <div className='flex flex-wrap gap-4 font-mono text-xs text-muted-foreground w-full md:w-auto pt-2 md:pt-0 border-t md:border-t-0 border-border/50 md:border-none'>
      <div className='flex items-center gap-1'>
       <Cpu className='w-3 h-3' />
       <span>VOICE: {isSessionActive ? 'READY' : 'OFF'}</span>
      </div>
      <div className='flex items-center gap-1'>
       <Activity className='w-3 h-3' />
       <span>LATENCY: {isSessionActive ? 'SYNCED' : '---'}</span>
      </div>
      <div className='flex items-center gap-1'>
       <SignalHigh className='w-3 h-3' />
       <span>ENCRYPTION: AES-256</span>
      </div>
     </div>
    </div>

    {/* Main Area */}
    <div className='flex-1 relative overflow-hidden bg-black/40 border border-border/50'>
     {!isSessionActive ? (
      <InterviewSetup
       initialProfileId={selectedProfileId}
       onStart={handleStartSession}
      />
     ) : (
      <InterviewTerminal
       sessionId={sessionId}
       jobProfileId={selectedProfileId}
       interviewType={interviewType}
      />
     )}
    </div>
   </div>
  </AppLayout>
 );
}
